import {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Loader } from 'Components';
import { Box } from '@mui/material';
import { RequestsContext } from 'Context';
import { IFormMetaData, createRequest, getRequestFields } from 'API';
import { NoteTextField } from './NoteTextField';
import { SuccessDialog } from '../SuccessDialog';
import {
  useAppSelector,
  useDictionary,
  useDisabilityCertAccess,
  useDispatchApp,
  useImposter,
  useIsDesktop,
} from 'Hooks';
import { ActionButtons } from './ActionButtons';
import { dataMapper } from './Utils';
import { getPersonalDetails } from 'API/personalDetails';
import { setPersonalDetails } from 'Store/PersonalDetailsSlice';
import { PersonalAreaRecommendation } from './PersonalAreaRecommendation';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { HTTP_STATUS_CODES } from 'Enum/HttpStatusCodes';
import { IDocumentTypeOption, IDocumentType } from 'Hooks';
import {
  IRON_SWORDS_FORM_ID,
  PERSONAL_RECOMMENDATION_URLS,
  DISABLED_VETERAN_SUB_ID,
} from 'Config';
import { ISubCategory } from 'API/UniversalRequest/universalRequest';
import { DocumentUploadManager } from './DocumentUploadManager';

export interface IFileUpload {
  subId: number;
  goBack: () => void;
  handleSuccess: () => void;
  resetRequest: () => void;
  titleLabel: string;
  onlineFormLink?: string;
  isDownload?: boolean;
  isOtp: boolean;
  subCategory?: ISubCategory;
}

export interface IError {
  isTypeError: boolean;
  isFileError: boolean;
  isServerError: boolean;
}

export const FileUpload = forwardRef<HTMLButtonElement, IFileUpload>(
  (props, ref) => {
    const {
      goBack,
      subId,
      handleSuccess,
      resetRequest,
      titleLabel,
      onlineFormLink,
      isDownload,
      isOtp,
      subCategory,
    } = props;

    const { requestsData, setRequestsData } = useContext(RequestsContext);
    const [isPageLoading, setIsPageLoading] = useState(true);
    const [uploadFilesList, setUploadFilesList] = useState<IFormMetaData>();
    const [note, setNote] = useState('');
    const [internetReqNum, setInternetReqNum] = useState(0);
    const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
    const [isNoteError, setIsNoteError] = useState<boolean>(false);
    const [uploadedDocumentTypes, setUploadedDocumentTypes] = useState<
      IDocumentType[]
    >([]);

    const isDesktop = useIsDesktop();
    const { imposterId } = useImposter();
    const { personalDetails } = useAppSelector(
      ({ personalDetails }) => personalDetails,
    );
    const location = useLocation();
    const locationState = location.state as {
      customNoteTitle?: string;
    } | null;

    const availableDocumentTypes = useMemo((): IDocumentTypeOption[] => {
      if (!uploadFilesList?.attachments) return [];

      return uploadFilesList.attachments.map((attachment) => ({
        type: attachment.id,
        label: attachment.name,
      }));
    }, [uploadFilesList]);

    const requiredDocumentTypes = useMemo((): IDocumentTypeOption[] => {
      if (!uploadFilesList?.attachments) return [];

      return uploadFilesList.attachments
        .filter((attachment) => attachment.isRequired)
        .map((attachment) => ({
          type: attachment.id,
          label: attachment.name,
        }));
    }, [uploadFilesList]);

    const getUploadFileList = useCallback(async () => {
      if (subId) {
        const getFields = await getRequestFields(subId);
        if (getFields) {
          setUploadFilesList(getFields);
        }
      }
    }, [subId]);

    const sanitizeNote = (noteText: string): string => {
      return noteText.replace(/\r?\n/g, ' ');
    };

    const syncToRequestsData = useCallback(() => {
      if (!uploadedDocumentTypes || uploadedDocumentTypes.length === 0) {
        const initialFields = (uploadFilesList?.attachments || [])
          .filter((field) => field.isRequired)
          .map((field) => ({
            fieldId: field.id,
            attachments: {},
          }));

        setRequestsData((prevRequestData) => ({
          ...prevRequestData,
          fieldAttachmentsData: initialFields,
          note: note,
          type: subId,
        }));
        return;
      }

      const fieldAttachmentsData = uploadedDocumentTypes.map((docType) => {
        const attachments: Record<string, File> = {};

        docType.files.forEach((uploadedFile: any) => {
          if (uploadedFile.file) {
            attachments[uploadedFile.name] = uploadedFile.file;
          }
        });

        return {
          fieldId: docType.type,
          attachments,
        };
      });

      setRequestsData((prevRequestData) => ({
        ...prevRequestData,
        fieldAttachmentsData,
        note: note,
        type: subId,
      }));
    }, [uploadedDocumentTypes, note, subId, setRequestsData, uploadFilesList]);

    useEffect(() => {
      syncToRequestsData();
    }, [syncToRequestsData]);

    const dictionary = useDictionary(Dictionary, DICTIONARY.UniversalRequest);

    useEffect(() => {
      if (isNoteError) {
        const el = document.getElementById('request-note');
        el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, [isNoteError]);

    const validateFormSubmitHandler = useCallback((): boolean => {
      let isValid = true;

      const isNoteEmpty = note.length === 0;
      if (isNoteEmpty) {
        setIsNoteError(true);
        isValid = false;
      }

      if (requiredDocumentTypes.length > 0) {
        requiredDocumentTypes.forEach((requiredType) => {
          const docType = uploadedDocumentTypes.find(
            (dt) => dt.type === requiredType.type,
          );

          if (!docType || !docType.files || docType.files.length === 0) {
            console.error(
              dictionary.uploadFile.requiredFieldMissing(requiredType.label),
            );
            isValid = false;
          }
        });
      }

      return isValid;
    }, [note, requiredDocumentTypes, uploadedDocumentTypes, dictionary]);

    const dispatch = useDispatchApp();

    const otpData = useAppSelector(
      ({ otpLoginDetails: { otpLoginDetails } }) => otpLoginDetails,
    );

    const initPersonalDetails = useCallback(async () => {
      setIsPageLoading(true);
      if (!uploadFilesList) getUploadFileList();

      if (!otpData) {
        const result = await getPersonalDetails();
        if (result) dispatch(setPersonalDetails(result));
      }
      setIsPageLoading(false);
    }, [dispatch, getUploadFileList, otpData, uploadFilesList]);

    useEffect(() => {
      window.scroll(0, 0);
      initPersonalDetails();
    }, [initPersonalDetails, imposterId]);

    const handleSubmit = async () => {
      setIsPageLoading(true);

      if (!validateFormSubmitHandler()) {
        setIsPageLoading(false);
        return;
      }

      syncToRequestsData();

      const response = await createRequest(
        dataMapper(
          { ...requestsData, note: sanitizeNote(note), type: subId },
          subId,
          otpData || personalDetails,
        ),
      );

      setIsPageLoading(false);
      if (response) {
        if (response.data?.internetReqNum) {
          setInternetReqNum(response.data.internetReqNum);
          setIsDialogOpen(true);
          handleSuccess();
        } else if (
          parseInt((response as any)?.response?.status) !==
          HTTP_STATUS_CODES.Forbidden
        ) {
          navigate('/error');
        }
      }
    };

    const onSetNote = (value: string) => {
      if (isNoteError && value.length > 0) {
        setIsNoteError(false);
      }
      setNote(value);
    };

    const navigate = useNavigate();

    const { hasAccess: hasVeteranDisabilityCert } = useDisabilityCertAccess();

    const shouldShowRecommendation = useCallback(() => {
      if (
        !isOtp &&
        subId === DISABLED_VETERAN_SUB_ID &&
        !hasVeteranDisabilityCert
      ) {
        return false;
      }
      return !!onlineFormLink && onlineFormLink !== '';
    }, [onlineFormLink, hasVeteranDisabilityCert, isOtp, subId]);

    useEffect(() => {
      if (subId === IRON_SWORDS_FORM_ID)
        setNote(dictionary.uploadFile.inputFor300);
    }, [dictionary.uploadFile.inputFor300, subId]);

    const [searchParams] = useSearchParams();

    useEffect(() => {
      const suppName = searchParams.get('suppName');
      const suppId = searchParams.get('suppId');

      if (suppName || suppId)
        onSetNote(dictionary.getSupplierNote(suppName || '', suppId || ''));
    }, []);

    const handleUploadComplete = useCallback((documentTypes: any[]) => {
      setUploadedDocumentTypes(documentTypes);
    }, []);

    return (
      <>
        {isPageLoading ? (
          <Loader />
        ) : isDialogOpen ? (
          <SuccessDialog
            title={titleLabel}
            userData={otpData || personalDetails}
            reqNum={internetReqNum}
            resetRequest={resetRequest}
          />
        ) : (
          <>
            {shouldShowRecommendation() && (
              <PersonalAreaRecommendation
                linkUrl={onlineFormLink || PERSONAL_RECOMMENDATION_URLS[subId]}
                isDownload={isDownload}
                isDisabledVeteran={
                  subId === DISABLED_VETERAN_SUB_ID &&
                  !isOtp &&
                  hasVeteranDisabilityCert
                }
                subCategory={subCategory}
              />
            )}
            <Box
              sx={
                isDesktop
                  ? {
                      width: '680px',
                      margin: '60px auto',
                    }
                  : { padding: '32px 24px' }
              }
            >
              <NoteTextField
                value={note}
                onChange={onSetNote}
                error={isNoteError}
                title={
                  locationState?.customNoteTitle
                    ? `${locationState.customNoteTitle} ${
                        Dictionary[DICTIONARY.General].required
                      }`
                    : subId === IRON_SWORDS_FORM_ID
                      ? dictionary.uploadFile.noteFor300
                      : undefined
                }
                titleVariant='body1'
                counterColor='#344266'
              />
              <DocumentUploadManager
                availableDocumentTypes={availableDocumentTypes}
                onUploadComplete={handleUploadComplete}
              />
              <ActionButtons handleBack={goBack} handleSubmit={handleSubmit} />
            </Box>
          </>
        )}
      </>
    );
  },
);
